// Provider catalog — TypeScript mirror of cloud-agent-backend's
// internal/providers package. Drives the dynamic key form (one component
// renders any provider) and pre-validates payloads client-side before
// submitting. The server-side catalog is the safety net; this mirror
// lets the UI fail fast and produce friendlier inline errors.
//
// Keep this file in sync with cloud-agent-backend/internal/providers/specs.go
// when adding/changing providers. The shapes are intentionally identical.

// ── Field-descriptor types ─────────────────────────────────────────────

export type FieldType = 'text' | 'password' | 'number' | 'textarea' | 'pem' | 'json' | 'stringList';

export interface FieldSpec {
  /** JSON-pointer-ish: "secret" | "metadata.<key>". */
  path: string;
  label: string;
  type: FieldType;
  required: boolean;
  /** True for fields that hold credential material — UI hides the value. */
  secret?: boolean;
  default?: unknown;
  placeholder?: string;
  help?: string;
}

// Capability bitmask values mirrored from cloud-agent-backend's
// internal/providers package and the "Provider" master table seeded by
// migration 0005. Frontends drive tab/section filtering by AND-ing
// against these bits — adding a new capability is a backend migration,
// not a frontend deploy.
//
//   CAP_STORAGE — cloud ops / resource discovery (S3/GCS/Blob, EC2, …)
//   CAP_AI      — LLM / embedding inference
//   CAP_COMPUTE — SSH and similar infra-runtime credentials
//   CAP_VCS     — source-code platforms (GitHub, future GitLab)
//   CAP_NOTIFY  — outbound notification transports (Slack, Teams, PagerDuty, SMTP)
//   CAP_GENERAL — catch-all for the legacy "OTHER" row
export const CAP_STORAGE = 0x1;
export const CAP_AI = 0x2;
export const CAP_COMPUTE = 0x4;
export const CAP_VCS = 0x8;
export const CAP_NOTIFY = 0x10;
export const CAP_GENERAL = 0xff;

export interface ProviderSpec {
  id: string;
  displayName: string;
  /**
   * Bitmask of CAP_* values describing what kinds of operations this
   * credential can drive. Sourced from the Provider master table; mirror
   * declared inline below so the frontend can render its tabs / filters
   * without a runtime round-trip.
   */
  capabilities: number;
  fields: FieldSpec[];
}

// ── Catalog ─────────────────────────────────────────────────────────────

export const PROVIDER_CATALOG: Record<string, ProviderSpec> = {
  AWS: {
    id: 'AWS',
    displayName: 'Amazon Web Services',
    capabilities: CAP_STORAGE,
    fields: [
      { path: 'metadata.accessKeyId', label: 'Access Key ID', type: 'text', required: true,
        placeholder: 'AKIAxxxxxxxxxxxxxxxx',
        help: 'The AKIA-prefixed identifier from IAM. Stored in plain text — only the secret access key is encrypted.' },
      { path: 'secret', label: 'Secret Access Key', type: 'password', required: true, secret: true,
        help: 'The 40-character secret. Encrypted at rest.' },
      { path: 'metadata.region', label: 'Region', type: 'text', required: false,
        placeholder: 'us-east-1',
        help: 'Default region for boto3 clients. Optional — leave blank to use the SDK default.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://s3.amazonaws.com',
        help: 'Override for non-AWS S3-compatible endpoints (MinIO, Wasabi, etc.). Most users leave blank.' },
    ],
  },
  BEDROCK: {
    id: 'BEDROCK',
    displayName: 'AWS Bedrock',
    capabilities: CAP_AI,
    fields: [
      { path: 'metadata.accessKeyId', label: 'Access Key ID', type: 'text', required: true,
        placeholder: 'AKIAxxxxxxxxxxxxxxxx',
        help: 'IAM access key allowed to invoke Bedrock. Stored in plain text — only the secret access key is encrypted.' },
      { path: 'secret', label: 'Secret Access Key', type: 'password', required: true, secret: true,
        help: 'The IAM secret. Encrypted at rest.' },
      { path: 'metadata.region', label: 'Region', type: 'text', required: false,
        placeholder: 'us-east-1',
        help: 'Default region for Bedrock Runtime. Optional — leave blank to use the SDK default.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://bedrock-runtime.us-east-1.amazonaws.com',
        help: 'Override for VPC endpoints or testing. Most users leave blank.' },
    ],
  },
  GCP: {
    id: 'GCP',
    displayName: 'Google Cloud',
    capabilities: CAP_STORAGE,
    fields: [
      { path: 'secret', label: 'Service Account JSON', type: 'json', required: true, secret: true,
        placeholder: '{"type":"service_account","project_id":"…","client_email":"…","private_key":"…"}',
        help: 'Paste the service-account JSON, or drop the file you downloaded from the IAM console. The full document is encrypted; project_id and client_email are extracted automatically for display.' },
    ],
  },
  AZURE: {
    id: 'AZURE',
    displayName: 'Microsoft Azure',
    capabilities: CAP_STORAGE,
    fields: [
      { path: 'metadata.clientId', label: 'Client ID (Application ID)', type: 'text', required: true,
        placeholder: '00000000-0000-0000-0000-000000000000' },
      { path: 'secret', label: 'Client Secret', type: 'password', required: true, secret: true,
        help: 'The secret value generated when you registered the app. Encrypted at rest.' },
      { path: 'metadata.tenantId', label: 'Tenant ID', type: 'text', required: true,
        placeholder: '00000000-0000-0000-0000-000000000000' },
      { path: 'metadata.subscriptionId', label: 'Subscription ID', type: 'text', required: false,
        placeholder: '00000000-0000-0000-0000-000000000000' },
    ],
  },
  OCI: {
    id: 'OCI',
    displayName: 'Oracle Cloud Infrastructure',
    capabilities: CAP_STORAGE,
    fields: [
      { path: 'metadata.tenancyOcid', label: 'Tenancy OCID', type: 'text', required: true,
        placeholder: 'ocid1.tenancy.oc1..aaaa…',
        help: "The tenancy's OCID, from the OCI console (Profile → Tenancy)." },
      { path: 'metadata.userOcid', label: 'User OCID', type: 'text', required: true,
        placeholder: 'ocid1.user.oc1..aaaa…',
        help: 'The OCID of the user the API signing key belongs to.' },
      { path: 'metadata.fingerprint', label: 'Key Fingerprint', type: 'text', required: true,
        placeholder: 'aa:bb:cc:dd:…',
        help: 'Fingerprint of the uploaded API signing key (shown next to the key in the console).' },
      { path: 'secret', label: 'API Private Key (PEM)', type: 'pem', required: true, secret: true,
        placeholder: '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----',
        help: 'Paste the PEM, or drop the .pem file. Passphrase-protected keys are rejected — the agent runs unattended. Encrypted at rest.' },
      { path: 'metadata.region', label: 'Region', type: 'text', required: false,
        placeholder: 'us-phoenix-1',
        help: 'Default region for the oci CLI / SDK. Optional — leave blank to use the SDK default.' },
    ],
  },
  SSH: {
    id: 'SSH',
    displayName: 'SSH',
    capabilities: CAP_COMPUTE,
    fields: [
      { path: 'metadata.user', label: 'User', type: 'text', required: true,
        placeholder: 'ubuntu',
        help: 'Linux user the agent will SSH as.' },
      { path: 'metadata.host', label: 'Host', type: 'text', required: true,
        placeholder: 'vps.example.com  or  *  for wildcard',
        help: "Hostname or IP. Use '*' for a key that's valid across multiple hosts; the agent will require the prompt to name the target host." },
      { path: 'metadata.port', label: 'Port', type: 'number', required: false, default: 22,
        placeholder: '22' },
      { path: 'secret', label: 'PEM Private Key', type: 'pem', required: true, secret: true,
        placeholder: '-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----',
        help: 'Paste the PEM, or drop the private key file (e.g. id_ed25519). Passphrase-encrypted keys are rejected — the agent runs unattended.' },
    ],
  },
  GITHUB: {
    id: 'GITHUB',
    displayName: 'GitHub',
    capabilities: CAP_VCS,
    fields: [
      { path: 'secret', label: 'Personal Access Token (PAT)', type: 'password', required: true, secret: true,
        placeholder: 'ghp_xxxxxxxxxxxxxxxxxxxxxxxx',
        help: 'A classic or fine-grained PAT. Encrypted at rest. The first 7 characters are kept in metadata for display.' },
      { path: 'metadata.org', label: 'Org (optional)', type: 'text', required: false,
        placeholder: 'my-github-org',
        help: 'Restrict this credential to a single org if you scoped the PAT that way. Optional — leave blank for personal-account access.' },
      { path: 'metadata.repoSlugs', label: 'Allowed Repos (optional)', type: 'stringList', required: false,
        placeholder: 'owner/repo',
        help: "Repos the agent is allowed to operate on with this credential, as 'owner/name'. Drives the awareness block injected into the planner. Leave empty to grant the full PAT scope (the agent will still only act on repos the user names in prompts)." },
      { path: 'metadata.protectedBranches', label: 'Protected Branches (optional)', type: 'stringList', required: false,
        placeholder: 'main',
        help: "Branches that require explicit user approval for write operations (push, merge, delete). 'main' / 'master' / 'release/*' are good defaults." },
    ],
  },
  GITLAB: {
    id: 'GITLAB',
    displayName: 'GitLab',
    capabilities: CAP_VCS,
    fields: [
      { path: 'secret', label: 'Access Token', type: 'password', required: true, secret: true,
        placeholder: 'glpat-xxxxxxxxxxxxxxxxxxxx',
        help: 'A personal, project, or group access token. Encrypted at rest. The first 9 characters are kept in metadata for display.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://gitlab.example.com/api/v4',
        help: 'Override for self-hosted GitLab. Leave blank to use https://gitlab.com/api/v4.' },
      { path: 'metadata.group', label: 'Top-level Group (optional)', type: 'text', required: false,
        placeholder: 'my-gitlab-group',
        help: 'Restrict this credential to a single top-level group if you scoped the token that way. Optional — leave blank for personal-account or wider scope.' },
      { path: 'metadata.repoSlugs', label: 'Allowed Projects (optional)', type: 'stringList', required: false,
        placeholder: 'group/subgroup/project',
        help: 'Project paths the agent is allowed to operate on with this credential. GitLab project paths can be arbitrarily deep (group/subgroup/.../project). Leave empty to grant the full token scope.' },
      { path: 'metadata.protectedBranches', label: 'Protected Branches (optional)', type: 'stringList', required: false,
        placeholder: 'main',
        help: "Branches that require explicit user approval for write operations. 'main' / 'master' / 'release/*' are good defaults." },
    ],
  },
  AZURE_DEVOPS: {
    id: 'AZURE_DEVOPS',
    displayName: 'Azure DevOps',
    capabilities: CAP_VCS,
    fields: [
      { path: 'secret', label: 'Personal Access Token (PAT)', type: 'password', required: true, secret: true,
        placeholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        help: 'An ADO PAT with the scopes you want the agent to use (Code, Build, Release, Work Items …). Encrypted at rest. The first 7 characters are kept in metadata for display.' },
      { path: 'metadata.organization', label: 'Organization', type: 'text', required: true,
        placeholder: 'my-ado-org',
        help: 'The ADO organization name (the segment after dev.azure.com/, or the prefix in your-org.visualstudio.com).' },
      { path: 'metadata.project', label: 'Project (optional)', type: 'text', required: false,
        placeholder: 'Engineering',
        help: 'Restrict this credential to a single ADO project. Optional — leave blank if the PAT spans multiple projects in the org.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://dev.azure.com',
        help: 'Override for ADO Server (on-prem) deployments. Leave blank to use https://dev.azure.com.' },
      { path: 'metadata.repoSlugs', label: 'Allowed Repos (optional)', type: 'stringList', required: false,
        placeholder: 'project/repo',
        help: "Repos the agent is allowed to operate on. Use 'project/repo' when 'Project' is blank, or just 'repo' when scoped to one project. Leave empty to grant the full PAT scope." },
      { path: 'metadata.protectedBranches', label: 'Protected Branches (optional)', type: 'stringList', required: false,
        placeholder: 'main',
        help: 'Branches that require explicit user approval for write operations and pipeline triggers.' },
    ],
  },
  OPENAI: {
    id: 'OPENAI',
    displayName: 'OpenAI',
    capabilities: CAP_AI,
    fields: [
      { path: 'secret', label: 'API Key', type: 'password', required: true, secret: true,
        placeholder: 'sk-…',
        help: 'Your OpenAI API key. Encrypted at rest. The first ~7 characters are kept in metadata for display.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://api.openai.com/v1',
        help: 'Override for OpenAI-compatible endpoints (Azure OpenAI, self-hosted gateways). Most users leave blank.' },
    ],
  },
  ANTHROPIC: {
    id: 'ANTHROPIC',
    displayName: 'Anthropic',
    capabilities: CAP_AI,
    fields: [
      { path: 'secret', label: 'API Key', type: 'password', required: true, secret: true,
        placeholder: 'sk-ant-…',
        help: 'Your Anthropic API key. Encrypted at rest. The first ~10 characters are kept in metadata for display.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://api.anthropic.com',
        help: 'Override for Anthropic-compatible endpoints. Most users leave blank.' },
    ],
  },
  GEMINI: {
    id: 'GEMINI',
    displayName: 'Google Gemini',
    capabilities: CAP_AI,
    fields: [
      { path: 'secret', label: 'API Key', type: 'password', required: true, secret: true,
        placeholder: 'AIza…',
        help: 'Your Gemini API key from Google AI Studio. Encrypted at rest. The first ~7 characters are kept in metadata for display.' },
      { path: 'metadata.endpoint', label: 'Endpoint (optional)', type: 'text', required: false,
        placeholder: 'https://generativelanguage.googleapis.com',
        help: 'Override for proxies or regional endpoints. Most users leave blank to use the default Gemini API base URL.' },
    ],
  },
  SLACK: {
    id: 'SLACK',
    displayName: 'Slack',
    capabilities: CAP_NOTIFY,
    fields: [
      { path: 'secret', label: 'Incoming Webhook URL', type: 'password', required: true, secret: true,
        placeholder: 'https://hooks.slack.com/services/T000/B000/xxxxxxxx',
        help: 'A Slack Incoming Webhook URL. The target channel is fixed by the webhook. Encrypted at rest; never injected into the sandbox.' },
      { path: 'metadata.channelHint', label: 'Channel Hint (optional)', type: 'text', required: false,
        placeholder: '#oncall',
        help: 'Display-only label for the channel this webhook posts to. Does not change routing — the webhook URL controls the destination.' },
    ],
  },
  TEAMS: {
    id: 'TEAMS',
    displayName: 'Microsoft Teams',
    capabilities: CAP_NOTIFY,
    fields: [
      { path: 'secret', label: 'Incoming Webhook URL', type: 'password', required: true, secret: true,
        placeholder: 'https://outlook.office.com/webhook/...',
        help: 'A Teams Incoming Webhook (connector) URL. The target channel is fixed by the webhook. Encrypted at rest; never injected into the sandbox.' },
    ],
  },
  PAGERDUTY: {
    id: 'PAGERDUTY',
    displayName: 'PagerDuty',
    capabilities: CAP_NOTIFY,
    fields: [
      { path: 'secret', label: 'Integration / Routing Key', type: 'password', required: true, secret: true,
        placeholder: 'R0XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX',
        help: 'An Events API v2 integration (routing) key from a PagerDuty service. Encrypted at rest; never injected into the sandbox.' },
    ],
  },
  SMTP: {
    id: 'SMTP',
    displayName: 'Email (SMTP)',
    capabilities: CAP_NOTIFY,
    fields: [
      { path: 'metadata.host', label: 'SMTP Host', type: 'text', required: true,
        placeholder: 'smtp.example.com',
        help: 'Hostname of the SMTP relay.' },
      { path: 'metadata.port', label: 'Port', type: 'number', required: false, default: 587,
        placeholder: '587',
        help: 'SMTP port. Defaults to 587 (STARTTLS). TLS is always required.' },
      { path: 'metadata.fromAddress', label: 'From Address', type: 'text', required: true,
        placeholder: 'alerts@example.com',
        help: 'Envelope-from / From header for messages the agent sends. Also used as the SMTP AUTH username.' },
      { path: 'metadata.toAddresses', label: 'To Addresses', type: 'stringList', required: true,
        placeholder: 'oncall@example.com',
        help: 'One or more recipient addresses. Every notify message is sent to all of them.' },
      { path: 'secret', label: 'Password', type: 'password', required: true, secret: true,
        help: 'SMTP AUTH password for the From address. Encrypted at rest; never injected into the sandbox.' },
    ],
  },
  TELEGRAM: {
    id: 'TELEGRAM',
    displayName: 'Telegram',
    capabilities: CAP_NOTIFY,
    fields: [
      { path: 'secret', label: 'Bot Token', type: 'password', required: true, secret: true,
        placeholder: '123456789:AAExxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
        help: 'Bot token from @BotFather. Encrypted at rest; never injected into the sandbox. One bot per project keeps projects isolated.' },
      { path: 'metadata.chatId', label: 'Chat ID', type: 'text', required: false,
        placeholder: '-1001234567890  or  @mychannel',
        help: 'Default chat the agent posts to. Use Detect to fill it automatically, or enter a numeric id (groups are negative) / @username for a public channel. Optional — leave blank for inbound-only (driving the agent from a chat); inbound replies go to whoever messages the bot.' },
      { path: 'metadata.threadId', label: 'Topic / Thread ID (optional)', type: 'number', required: false,
        placeholder: '12',
        help: 'For forum-style groups, the message_thread_id to post into. Leave blank for the main chat.' },
    ],
  },
};

// Stable display order for dropdowns. Matches the Go catalog's List().
const PROVIDER_ORDER = ['AWS', 'BEDROCK', 'GCP', 'AZURE', 'OCI', 'SSH', 'GITHUB', 'GITLAB', 'AZURE_DEVOPS', 'OPENAI', 'ANTHROPIC', 'GEMINI', 'SLACK', 'TEAMS', 'PAGERDUTY', 'SMTP', 'TELEGRAM'];

export function listProviders(): ProviderSpec[] {
  return PROVIDER_ORDER.flatMap((id) => (PROVIDER_CATALOG[id] ? [PROVIDER_CATALOG[id]] : []));
}

export function getProvider(id: string): ProviderSpec | undefined {
  return PROVIDER_CATALOG[id];
}

// ── Path helpers ───────────────────────────────────────────────────────

const SECRET_PATH = 'secret';
const METADATA_PREFIX = 'metadata.';

export function isSecretPath(path: string): boolean {
  return path === SECRET_PATH;
}

export function isMetadataPath(path: string): path is `metadata.${string}` {
  return path.startsWith(METADATA_PREFIX);
}

export function metadataKey(path: string): string | undefined {
  return isMetadataPath(path) ? path.slice(METADATA_PREFIX.length) : undefined;
}

/** getValueAt reads the FieldSpec.path from a separate (secret, metadata) tuple. */
export function getValueAt(secret: string, metadata: Record<string, unknown>, path: string): unknown {
  if (path === SECRET_PATH) return secret;
  const k = metadataKey(path);
  return k ? metadata[k] : undefined;
}

/** setValueAt writes to (secret, metadata). Returns the updated tuple. */
export function setValueAt(
  secret: string,
  metadata: Record<string, unknown>,
  path: string,
  value: unknown,
): { secret: string; metadata: Record<string, unknown> } {
  if (path === SECRET_PATH) return { secret: typeof value === 'string' ? value : '', metadata };
  const k = metadataKey(path);
  if (!k) return { secret, metadata };
  const nextMeta = { ...metadata };
  if (value === undefined || value === null || value === '') delete nextMeta[k];
  else nextMeta[k] = value;
  return { secret, metadata: nextMeta };
}

// ── Validation ─────────────────────────────────────────────────────────

/**
 * validate runs the structural checks for a credential payload. Returns
 * an error string on failure, or null on success. Mirrors the Go-side
 * Validate() in internal/providers/validators.go.
 */
export function validate(
  providerId: string,
  label: string,
  secret: string,
  metadata: Record<string, unknown>,
): string | null {
  const spec = getProvider(providerId);
  if (!spec) {
    return `Unknown provider "${providerId}". Known providers: ${Object.keys(PROVIDER_CATALOG).join(', ')}`;
  }
  if (!label || label.trim() === '') {
    return 'Label is required';
  }

  // Required-field + type checks.
  for (const f of spec.fields) {
    const present = isFieldPresent(secret, metadata, f.path);
    if (f.required && !present) {
      return `${spec.id}.${f.label} is required`;
    }
    if (!present) continue;
    const value = getValueAt(secret, metadata, f.path);
    const typeErr = typeCheck(f, value);
    if (typeErr) return typeErr;
  }

  // Reject any caller-supplied metadata keys not declared in the spec
  // and any reserved underscore-prefixed keys (catalog-managed).
  const allowed = allowedMetadataKeys(spec);
  for (const k of Object.keys(metadata)) {
    if (k.startsWith('_')) {
      return `Metadata key "${k}" is reserved (the leading underscore is catalog-managed); remove it from the payload`;
    }
    if (!allowed.has(k)) {
      return `Metadata key "${k}" is not part of the ${spec.id} spec. Allowed keys: ${[...allowed].join(', ')}`;
    }
  }
  return null;
}

function isFieldPresent(secret: string, metadata: Record<string, unknown>, path: string): boolean {
  const v = getValueAt(secret, metadata, path);
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  // Mirror the Go getAtPath: empty arrays count as absent so required-field
  // checks fire and stringList fields can be cleared by submitting [].
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function typeCheck(f: FieldSpec, value: unknown): string | null {
  switch (f.type) {
    case 'text':
    case 'password':
    case 'textarea':
      return typeof value === 'string' ? null : `${f.label} must be a string`;
    case 'number':
      if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return null;
      if (typeof value === 'string' && /^\d+$/.test(value)) return null;
      return `${f.label} must be a non-negative integer`;
    case 'json':
      if (typeof value !== 'string') return `${f.label} must be a JSON string`;
      try {
        JSON.parse(value);
        return null;
      } catch {
        return `${f.label} is not valid JSON`;
      }
    case 'pem':
      if (typeof value !== 'string') return `${f.label} must be a PEM string`;
      return validatePEM(value, f.label);
    case 'stringList': {
      if (!Array.isArray(value)) return `${f.label} must be a list of strings`;
      for (let i = 0; i < value.length; i++) {
        const item = value[i];
        if (typeof item !== 'string') return `${f.label}[${i}] must be a string`;
        if (item.trim() === '') return `${f.label}[${i}] is empty`;
      }
      return null;
    }
    default:
      return `${f.label} has unsupported field type ${f.type}`;
  }
}

const PEM_BEGIN_RE = /-----BEGIN [^-]+-----/;
const PEM_END_RE = /-----END [^-]+-----/;
const PEM_PROC_TYPE_ENCRYPTED = /^Proc-Type:\s*\d+,\s*ENCRYPTED/m;
const PEM_DEK_INFO = /^DEK-Info:/m;

function validatePEM(s: string, label: string): string | null {
  const t = s.trim();
  if (!t) return `${label}: empty PEM`;
  if (!PEM_BEGIN_RE.test(t) || !PEM_END_RE.test(t)) {
    return `${label}: no valid PEM block found (expected '-----BEGIN ... PRIVATE KEY-----')`;
  }
  if (PEM_PROC_TYPE_ENCRYPTED.test(t) || PEM_DEK_INFO.test(t)) {
    return `${label}: passphrase-encrypted PEM is not supported (the agent runs unattended)`;
  }
  return null;
}

function allowedMetadataKeys(spec: ProviderSpec): Set<string> {
  const out = new Set<string>();
  for (const f of spec.fields) {
    const k = metadataKey(f.path);
    if (k) out.add(k);
  }
  // Mirror the Go-side allow-list of SecretParser-derived metadata keys
  // so a round-trip read→edit→write doesn't trip the unknown-key check.
  switch (spec.id) {
    case 'GCP':
      out.add('clientEmail');
      out.add('projectId');
      break;
    case 'GITHUB':
    case 'AZURE_DEVOPS':
    case 'OPENAI':
    case 'ANTHROPIC':
    case 'GEMINI':
      out.add('tokenPrefix');
      break;
    case 'GITLAB':
      out.add('tokenPrefix');
      out.add('host'); // derived from endpoint by the parser
      break;
  }
  return out;
}

// ── Display hints ──────────────────────────────────────────────────────
//
// Used for client-side previews when forms are filled in but not yet
// submitted. The persisted value comes from the server (cached in
// metadata._displayHint) — this function is a fallback / preview helper.

export function computeDisplayHint(providerId: string, metadata: Record<string, unknown>): string {
  const cached = metadata['_displayHint'];
  if (typeof cached === 'string' && cached) return cached;

  switch (providerId) {
    case 'AWS':
    case 'BEDROCK': {
      const key = str(metadata['accessKeyId']);
      const region = str(metadata['region']);
      return shortPrefix(key, 8) + (region ? ` / ${region}` : '');
    }
    case 'GCP': {
      const email = str(metadata['clientEmail']);
      const proj = str(metadata['projectId']);
      if (!email && !proj) return '';
      if (!email) return proj;
      if (!proj) return email;
      return `${email} / ${proj}`;
    }
    case 'AZURE': {
      const client = str(metadata['clientId']);
      const sub = str(metadata['subscriptionId']);
      const head = shortPrefix(client, 8);
      return head + (sub ? ` / sub:${shortPrefix(sub, 8)}` : '');
    }
    case 'OCI': {
      const tenancy = str(metadata['tenancyOcid']);
      const region = str(metadata['region']);
      const head = shortPrefix(tenancy, 12);
      return head + (region ? ` / ${region}` : '');
    }
    case 'SSH': {
      const user = str(metadata['user']);
      const host = str(metadata['host']);
      const port = metadata['port'];
      let portStr = '';
      if (typeof port === 'number' && port > 0 && port !== 22) portStr = `:${port}`;
      else if (typeof port === 'string' && /^\d+$/.test(port) && port !== '22') portStr = `:${port}`;
      if (!user && !host) return '';
      return `${user}@${host}${portStr}`.replace(/^@/, '');
    }
    case 'GITHUB': {
      const prefix = str(metadata['tokenPrefix']);
      const org = str(metadata['org']);
      const head = prefix ? `${prefix}…` : '';
      return [head, org].filter(Boolean).join(' / ');
    }
    case 'GITLAB': {
      const prefix = str(metadata['tokenPrefix']);
      const group = str(metadata['group']);
      let host = str(metadata['host']);
      if (!host) {
        const endpoint = str(metadata['endpoint']);
        host = endpoint ? hostFromURL(endpoint) : 'gitlab.com';
      }
      let out = prefix ? `${prefix}…` : '';
      if (group) out = out ? `${out} / ${group}` : group;
      if (host && host !== 'gitlab.com') out = out ? `${out} @ ${host}` : host;
      return out;
    }
    case 'AZURE_DEVOPS': {
      const prefix = str(metadata['tokenPrefix']);
      const org = str(metadata['organization']);
      const project = str(metadata['project']);
      let out = prefix ? `${prefix}…` : '';
      if (org) {
        const orgSeg = project ? `${org}/${project}` : org;
        out = out ? `${out} / ${orgSeg}` : orgSeg;
      }
      return out;
    }
    case 'OPENAI':
    case 'ANTHROPIC':
    case 'GEMINI': {
      const prefix = str(metadata['tokenPrefix']);
      return prefix ? `${prefix}…` : '';
    }
    case 'SLACK':
      return str(metadata['channelHint']);
    case 'TELEGRAM':
      return str(metadata['chatId']);
    case 'SMTP': {
      const from = str(metadata['fromAddress']);
      const host = str(metadata['host']);
      return from + (host ? ` @ ${host}` : '');
    }
    default:
      return '';
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function shortPrefix(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n) + '…';
}

function hostFromURL(raw: string): string {
  let s = raw.trim();
  const schemeIdx = s.indexOf('://');
  if (schemeIdx >= 0) s = s.slice(schemeIdx + 3);
  const cut = s.search(/[/?#]/);
  if (cut >= 0) s = s.slice(0, cut);
  return s;
}
